import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Plus, ArrowLeft, CalendarDays, Clock, GripVertical, Music, Trash2, Copy, Send,
  Check, X, HelpCircle, MessageSquare, UserPlus, Rocket, List, LayoutGrid, CalendarRange,
  Share2, FileText, Link2, ChevronLeft, ChevronRight, MessageCircle, CalendarPlus, Repeat,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, StatusBadge, KeyBadge, BpmBadge, Field, inputCls } from "@/components/ui-kit";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }); }
  catch (e) { return d; }
}

const SERVICE_TYPES = ["Sunday Morning", "Sunday Evening", "Youth", "Prayer Night", "Communion", "Christmas", "Easter", "Special Event"];

/* ================= SERVICES LIST / ROSTER ================= */
export function Services() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [services, setServices] = useState([]);
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState({});
  const [team, setTeam] = useState([]);
  const [view, setView] = useState("list");
  const [cursor, setCursor] = useState(new Date());
  const [f, setF] = useState({ title: "Sunday Morning Worship", date: "", time: "09:00", type: "Sunday Morning", template: "Sunday Morning" });
  const canManage = ["super_admin", "worship_director", "worship_leader"].includes(user?.role);

  const load = () => api.get("/services").then((r) => setServices(r.data));
  useEffect(() => { load(); api.get("/templates").then((r) => setTemplates(r.data)); api.get("/team").then((r) => setTeam(r.data)); }, []);

  const create = async () => {
    if (!f.date) { toast.error("Pick a date"); return; }
    try { const { data } = await api.post("/services", f); setOpen(false); nav(`/services/${data.id}`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const duplicate = async (id, e) => { e.stopPropagation(); const { data } = await api.post(`/services/${id}/duplicate`); toast.success("Service duplicated"); load(); };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = services.filter((s) => s.date >= today);
  const past = services.filter((s) => s.date < today);

  const Card = ({ s }) => {
    const confirmed = (s.team || []).filter((a) => a.status === "confirmed").length;
    return (
      <button data-testid={`service-card-${s.id}`} onClick={() => nav(`/services/${s.id}`)}
        className="wf-card rounded-2xl p-5 text-left w-full transition-all hover:-translate-y-0.5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="eyebrow mb-1">{s.type}</div>
            <div className="font-display text-lg font-bold text-[#F7F5F0]">{s.title}</div>
            <div className="flex items-center gap-3 text-xs text-[#8A97AC] mt-2">
              <span className="flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" /> {fmtDate(s.date)}</span>
              <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {s.time}</span>
            </div>
          </div>
          <StatusBadge status={s.status} />
        </div>
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5 text-xs text-[#8A97AC]">
          <span>{s.songs?.length || 0} songs · {confirmed}/{(s.team || []).length} confirmed</span>
          {canManage && <span onClick={(e) => duplicate(s.id, e)} className="flex items-center gap-1 text-[#8A97AC] hover:text-[#E5C378]"><Copy className="w-3.5 h-3.5" /> Copy</span>}
        </div>
      </button>
    );
  };

  return (
    <div>
      <PageHeader eyebrow="Plan the gathering" title="Services & Roster"
        subtitle="Build service plans, assign your team, and publish schedules."
        actions={canManage && (
          <div className="flex items-center gap-2">
          <RosterShareDialog />
          <GenerateMonthDialog templates={templates} onDone={load} />
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><button data-testid="btn-create-service" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl wf-gold-btn font-semibold text-sm"><Plus className="w-4 h-4" /> New service</button></DialogTrigger>
            <DialogContent className="wf-glass border-white/10 max-w-md">
              <DialogHeader><DialogTitle className="font-display text-[#F7F5F0]">Create service</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <Field label="Title"><input className={inputCls} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="service-title-input" /></Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Date"><input type="date" className={inputCls} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="service-date-input" /></Field>
                  <Field label="Time"><input type="time" className={inputCls} value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} /></Field>
                </div>
                <Field label="Template"><select className={inputCls} value={f.template} onChange={(e) => setF({ ...f, template: e.target.value, type: e.target.value })}>{Object.keys(templates).map((t) => <option key={t}>{t}</option>)}</select></Field>
                <button onClick={create} data-testid="save-service-button" className="w-full py-2.5 rounded-xl wf-gold-btn font-semibold text-sm">Create service</button>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        )} />

      <div className="flex items-center gap-1 mb-5 bg-[#0B101D] border border-white/10 rounded-xl p-1 w-fit">
        {[["list", "List", List], ["month", "Month", LayoutGrid], ["week", "Week", CalendarRange]].map(([v, label, Icon]) => (
          <button key={v} onClick={() => setView(v)} data-testid={`roster-view-${v}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${view === v ? "bg-[#E5C378]/15 text-[#E5C378]" : "text-[#8A97AC]"}`}>
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {services.length === 0 ? (
        <EmptyState icon={CalendarDays} title="No service scheduled" body="Create your next worship service to start planning." />
      ) : view === "list" ? (
        <>
          <div className="eyebrow mb-3">Upcoming</div>
          {upcoming.length === 0 ? <p className="text-sm text-[#6E7D97] mb-6">Nothing upcoming.</p> :
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">{upcoming.map((s) => <Card key={s.id} s={s} />)}</div>}
          {past.length > 0 && <>
            <div className="eyebrow mb-3">Service history</div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{past.map((s) => <Card key={s.id} s={s} />)}</div>
          </>}
        </>
      ) : view === "month" ? (
        <MonthView services={services} cursor={cursor} setCursor={setCursor} nav={nav} canManage={canManage} team={team} reload={load} />
      ) : (
        <WeekView services={services} cursor={cursor} setCursor={setCursor} nav={nav} />
      )}
    </div>
  );
}

function svcMap(services) {
  const m = {}; services.forEach((s) => { (m[s.date] = m[s.date] || []).push(s); }); return m;
}
const STATUS_DOT = { draft: "bg-gray-400", preparing: "bg-amber-400", ready: "bg-sky-400", published: "bg-emerald-400", completed: "bg-gray-500" };

function MonthView({ services, cursor, setCursor, nav, canManage, team = [], reload }) {
  const map = useMemo(() => svcMap(services), [services]);
  const year = cursor.getFullYear(), month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startOffset = (first.getDay() + 6) % 7; // Monday-first
  const cells = Array.from({ length: 42 }, (_, i) => new Date(year, month, i - startOffset + 1));
  const today = new Date().toISOString().slice(0, 10);
  const [selected, setSelected] = useState(today);
  const [avail, setAvail] = useState({});
  const [dragId, setDragId] = useState(null);

  useEffect(() => { if (selected) api.get(`/availability/resolve?date=${selected}`).then((r) => setAvail(r.data)).catch(() => setAvail({})); }, [selected, services]);
  const selectedServices = map[selected] || [];

  const assign = async (serviceId, memberId) => {
    const m = team.find((x) => x.id === memberId); if (!m) return;
    const svc = services.find((s) => s.id === serviceId);
    if (avail[memberId]?.status === "unavailable" &&
        !window.confirm(`${m.name} marked themselves unavailable on this day. Assign anyway?`)) return;
    if ((svc?.team || []).some((a) => a.member_id === memberId)) { toast.info(`${m.name} is already on this service`); return; }
    try {
      await api.post(`/services/${serviceId}/team`, { member_id: m.id, name: m.name, slot: m.instrument || "Vocals" });
      toast.success(`${m.name} added to ${svc?.title || "service"}`);
      reload && reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setStatus = async (serviceId, aid, status) => {
    try { await api.post(`/services/${serviceId}/team/${aid}/respond`, { status }); reload && reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const removeA = async (serviceId, aid) => { try { await api.delete(`/services/${serviceId}/team/${aid}`); reload && reload(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };

  const availDot = (mid) => {
    const st = avail[mid]?.status;
    const cls = st === "available" ? "bg-emerald-400" : st === "maybe" ? "bg-amber-400" : st === "unavailable" ? "bg-rose-400" : "bg-gray-600";
    return <span className={`w-2 h-2 rounded-full shrink-0 ${cls}`} title={st || "unknown"} />;
  };

  return (
    <div className={canManage ? "grid lg:grid-cols-[1fr_320px] gap-4 items-start" : ""}>
      <div className="wf-card rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="p-2 rounded-lg hover:bg-white/5 text-[#B8C1D1]"><ChevronLeft className="w-4 h-4" /></button>
          <div className="font-display font-semibold text-[#F7F5F0]">{cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</div>
          <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="p-2 rounded-lg hover:bg-white/5 text-[#B8C1D1]"><ChevronRight className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-7 gap-1.5">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="text-[10px] text-center text-[#6E7D97] font-semibold uppercase tracking-wider py-1">{d}</div>)}
          {cells.map((d, i) => {
            const ds = d.toISOString().slice(0, 10);
            const inMonth = d.getMonth() === month;
            const dayServices = map[ds] || [];
            const isSel = ds === selected;
            return (
              <div key={i} onClick={() => canManage && setSelected(ds)}
                className={`min-h-[84px] rounded-lg p-1.5 border flex flex-col gap-1 ${canManage ? "cursor-pointer" : ""} ${isSel ? "border-[#E5C378] bg-[#141F38]" : ds === today ? "border-[#E5C378]/50 bg-[#131D33]" : "border-white/5 bg-[#0E1524]"} ${inMonth ? "" : "opacity-40"}`}>
                <div className="text-[11px] text-[#8A97AC] font-mono">{d.getDate()}</div>
                {dayServices.map((s) => {
                  const confirmed = (s.team || []).filter((a) => a.status === "confirmed").length;
                  return (
                    <div key={s.id}
                      onDragOver={(e) => { if (canManage && dragId) e.preventDefault(); }}
                      onDrop={(e) => { e.stopPropagation(); const mid = e.dataTransfer.getData("memberId"); if (mid) assign(s.id, mid); setDragId(null); }}
                      className={dragId ? "ring-1 ring-[#E5C378]/50 rounded" : ""}>
                      <button onClick={(e) => { e.stopPropagation(); nav(`/services/${s.id}`); }} data-testid={`calendar-service-${s.id}`}
                        className="w-full text-left text-[10px] leading-tight px-1.5 py-1 rounded bg-[#E5C378]/12 border border-[#E5C378]/25 text-[#F7F5F0] hover:bg-[#E5C378]/20">
                        <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 ${STATUS_DOT[s.status]}`} />{s.time} {s.title}
                        <span className="block text-[9px] text-[#8A97AC]">{confirmed}/{(s.team || []).length} confirmed</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      {canManage && (
        <div className="wf-card rounded-2xl p-4 lg:sticky lg:top-4" data-testid="roster-panel">
          <div className="eyebrow mb-2">{fmtDate(selected)}</div>
          {selectedServices.length === 0 ? (
            <p className="text-sm text-[#6E7D97] mb-4">No service on this day. Click a day with a service to assign your team.</p>
          ) : selectedServices.map((s) => (
            <div key={s.id} className="mb-4"
              onDragOver={(e) => { if (dragId) e.preventDefault(); }}
              onDrop={(e) => { const mid = e.dataTransfer.getData("memberId"); if (mid) assign(s.id, mid); setDragId(null); }}>
              <div className="text-sm font-semibold text-[#F7F5F0] mb-1">{s.time} · {s.title}</div>
              <div className={`rounded-xl border border-dashed p-2 min-h-[52px] space-y-1.5 ${dragId ? "border-[#E5C378]/60 bg-[#E5C378]/5" : "border-white/10"}`} data-testid={`dropzone-${s.id}`}>
                {(s.team || []).length === 0 && <div className="text-[11px] text-[#6E7D97] text-center py-2">Drag a member here to assign</div>}
                {(s.team || []).map((a) => (
                  <div key={a.id} className="flex items-center gap-1.5 bg-[#0B101D] border border-white/5 rounded-lg px-2 py-1.5">
                    <div className="min-w-0 flex-1"><div className="text-xs text-[#F7F5F0] truncate">{a.name}</div><div className="text-[10px] text-[#E5C378]">{a.slot}</div></div>
                    <select value={a.status} onChange={(e) => setStatus(s.id, a.id, e.target.value)} data-testid={`panel-status-${a.id}`}
                      className="bg-[#0B101D] border border-white/10 rounded-md px-1 py-1 text-[10px] text-[#F7F5F0] capitalize">
                      {["pending", "confirmed", "maybe", "declined"].map((st) => <option key={st} value={st}>{st}</option>)}
                    </select>
                    <button onClick={() => removeA(s.id, a.id)} className="text-[#6E7D97] hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}

          <div className="eyebrow mb-2 mt-3">Team — drag onto a service</div>
          <div className="space-y-1 max-h-72 overflow-auto pr-1">
            {team.filter((m) => m.active !== false).map((m) => (
              <div key={m.id} draggable
                onDragStart={(e) => { e.dataTransfer.setData("memberId", m.id); setDragId(m.id); }}
                onDragEnd={() => setDragId(null)}
                data-testid={`drag-member-${m.id}`}
                className="flex items-center gap-2 bg-[#0B101D] border border-white/5 rounded-lg px-2.5 py-2 cursor-grab active:cursor-grabbing hover:border-[#E5C378]/30">
                {availDot(m.id)}
                <div className="min-w-0 flex-1"><div className="text-xs text-[#F7F5F0] truncate">{m.name}</div><div className="text-[10px] text-[#8A97AC] truncate">{m.instrument}</div></div>
                <GripVertical className="w-3.5 h-3.5 text-[#6E7D97]" />
              </div>
            ))}
          </div>
          <div className="flex items-center gap-3 mt-2 text-[10px] text-[#6E7D97]">
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-400" /> available</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400" /> maybe</span>
            <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-rose-400" /> unavailable</span>
          </div>
        </div>
      )}
    </div>
  );
}

function WeekView({ services, cursor, setCursor, nav }) {
  const map = useMemo(() => svcMap(services), [services]);
  const base = new Date(cursor);
  const monday = new Date(base); monday.setDate(base.getDate() - ((base.getDay() + 6) % 7));
  const days = Array.from({ length: 7 }, (_, i) => { const d = new Date(monday); d.setDate(monday.getDate() + i); return d; });
  const today = new Date().toISOString().slice(0, 10);
  return (
    <div className="wf-card rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => { const n = new Date(cursor); n.setDate(cursor.getDate() - 7); setCursor(n); }} className="p-2 rounded-lg hover:bg-white/5 text-[#B8C1D1]"><ChevronLeft className="w-4 h-4" /></button>
        <div className="font-display font-semibold text-[#F7F5F0]">Week of {monday.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
        <button onClick={() => { const n = new Date(cursor); n.setDate(cursor.getDate() + 7); setCursor(n); }} className="p-2 rounded-lg hover:bg-white/5 text-[#B8C1D1]"><ChevronRight className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
        {days.map((d) => {
          const ds = d.toISOString().slice(0, 10);
          const dayServices = map[ds] || [];
          return (
            <div key={ds} className={`rounded-xl p-2.5 border min-h-[120px] ${ds === today ? "border-[#E5C378]/50 bg-[#131D33]" : "border-white/5 bg-[#0E1524]"}`}>
              <div className="text-xs text-[#B8C1D1] font-semibold mb-2">{d.toLocaleDateString(undefined, { weekday: "short", day: "numeric" })}</div>
              {dayServices.length === 0 ? <div className="text-[10px] text-[#6E7D97]">—</div> : dayServices.map((s) => {
                const confirmed = (s.team || []).filter((a) => a.status === "confirmed").length;
                return (
                  <button key={s.id} onClick={() => nav(`/services/${s.id}`)} className="w-full text-left mb-2 p-2 rounded-lg bg-[#E5C378]/10 border border-[#E5C378]/25 hover:bg-[#E5C378]/20">
                    <div className="text-xs text-[#F7F5F0] font-medium">{s.time} {s.title}</div>
                    <div className="text-[10px] text-[#8A97AC] mt-0.5">{confirmed}/{(s.team || []).length} confirmed</div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

const WEEKDAYS = [["Sunday", 6], ["Monday", 0], ["Tuesday", 1], ["Wednesday", 2], ["Thursday", 3], ["Friday", 4], ["Saturday", 5]];

function RosterShareDialog() {
  const [open, setOpen] = useState(false);
  const now = new Date();
  const mStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
  const mEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
  const [start, setStart] = useState(mStart);
  const [end, setEnd] = useState(mEnd);
  const [title, setTitle] = useState("");
  const [onlyPublished, setOnlyPublished] = useState(false);
  const [includeCharts, setIncludeCharts] = useState(true);
  const [shares, setShares] = useState([]);

  const loadShares = () => api.get("/rosters/shares").then((r) => setShares(r.data)).catch(() => {});
  useEffect(() => { if (open) loadShares(); }, [open]);

  const linkFor = (id) => `${window.location.origin}/roster/${id}`;
  const create = async () => {
    if (!start || !end || start > end) { toast.error("Pick a valid start and end date"); return; }
    try {
      const { data } = await api.post("/rosters/share", { title, start, end, only_published: onlyPublished, include_charts: includeCharts });
      toast.success("Roster link created & copied");
      try { await navigator.clipboard.writeText(linkFor(data.share_id)); } catch (e) {}
      loadShares();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const copy = (id) => { navigator.clipboard.writeText(linkFor(id)); toast.success("Link copied"); };
  const whatsapp = (s) => window.open(`https://wa.me/?text=${encodeURIComponent(`${s.title}: ${linkFor(s.share_id)}`)}`, "_blank");
  const disable = async (id) => { try { await api.delete(`/rosters/shares/${id}`); loadShares(); toast.success("Link disabled"); } catch (e) {} };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="btn-share-roster" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-[#F7F5F0] font-semibold text-sm hover:border-[#E5C378]/30">
          <Share2 className="w-4 h-4" /> Share roster
        </button>
      </DialogTrigger>
      <DialogContent className="wf-glass border-white/10 max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-[#F7F5F0]">Share a roster with the team</DialogTitle></DialogHeader>
        <p className="text-xs text-[#8A97AC] -mt-1">Creates a public, no-login link (great for pinning in a WhatsApp group) showing a timetable of every service in the range.</p>

        <div className="flex gap-2">
          <button onClick={() => { setStart(mStart); setEnd(mEnd); }} data-testid="range-this-month" className="px-3 py-1.5 rounded-lg bg-[#E5C378]/12 border border-[#E5C378]/30 text-xs text-[#E5C378]">This month</button>
          <button onClick={() => { const s = new Date(now.getFullYear(), now.getMonth() + 1, 1); setStart(s.toISOString().slice(0, 10)); setEnd(new Date(now.getFullYear(), now.getMonth() + 2, 0).toISOString().slice(0, 10)); }} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-[#B8C1D1]">Next month</button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="From"><input type="date" className={inputCls} value={start} onChange={(e) => setStart(e.target.value)} data-testid="roster-start" /></Field>
          <Field label="To"><input type="date" className={inputCls} value={end} onChange={(e) => setEnd(e.target.value)} data-testid="roster-end" /></Field>
        </div>
        <Field label="Title (optional)"><input className={inputCls} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. June Worship Roster" data-testid="roster-share-title" /></Field>
        <div className="flex flex-wrap gap-4 text-xs text-[#B8C1D1]">
          <label className="flex items-center gap-2"><input type="checkbox" checked={onlyPublished} onChange={(e) => setOnlyPublished(e.target.checked)} data-testid="roster-only-published" /> Only published services</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={includeCharts} onChange={(e) => setIncludeCharts(e.target.checked)} data-testid="roster-include-charts" /> Let team open charts/lyrics</label>
        </div>
        <button onClick={create} data-testid="create-roster-link" className="w-full py-2.5 rounded-xl wf-gold-btn font-semibold text-sm">Create shareable link</button>

        {shares.length > 0 && (
          <div className="mt-1 border-t border-white/10 pt-3 max-h-52 overflow-auto space-y-2">
            <div className="eyebrow">Active links</div>
            {shares.map((s) => (
              <div key={s.share_id} data-testid={`roster-share-${s.share_id}`} className="bg-[#0B101D] border border-white/10 rounded-xl p-2.5">
                <div className="text-sm text-[#F7F5F0]">{s.title}</div>
                <div className="text-[11px] text-[#6E7D97] mb-2">{s.start} → {s.end}{s.only_published ? " · published only" : ""}</div>
                <div className="flex flex-wrap gap-1.5">
                  <button onClick={() => copy(s.share_id)} data-testid={`copy-roster-${s.share_id}`} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-[#F7F5F0]"><Link2 className="w-3.5 h-3.5" /> Copy</button>
                  <button onClick={() => whatsapp(s)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-xs text-emerald-300"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</button>
                  <a href={linkFor(s.share_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-[#B8C1D1]">Open</a>
                  <button onClick={() => disable(s.share_id)} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/5 border border-white/10 text-xs text-[#8A97AC] hover:text-rose-400 ml-auto"><X className="w-3.5 h-3.5" /> Disable</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function GenerateMonthDialog({ templates, onDone }) {
  const [open, setOpen] = useState(false);
  const now = new Date();
  const [ym, setYm] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [skipExisting, setSkipExisting] = useState(true);
  const [patterns, setPatterns] = useState([{ title: "Sunday Morning Worship", time: "09:00", template: "Sunday Morning", type: "Sunday Morning", weekday: 6 }]);
  const [savedSets, setSavedSets] = useState([]);
  const tplNames = Object.keys(templates || {});

  const loadSaved = () => api.get("/roster-templates").then((r) => setSavedSets(r.data)).catch(() => {});
  useEffect(() => { if (open) loadSaved(); }, [open]);

  const setP = (i, fields) => setPatterns(patterns.map((p, idx) => (idx === i ? { ...p, ...fields } : p)));
  const addP = () => setPatterns([...patterns, { title: "Youth Service", time: "18:30", template: "Youth", type: "Youth", weekday: 2 }]);
  const removeP = (i) => setPatterns(patterns.filter((_, idx) => idx !== i));

  const applySaved = (id) => {
    const t = savedSets.find((x) => x.id === id);
    if (t) { setPatterns(t.patterns.map((p) => ({ ...p }))); toast.success(`Loaded "${t.name}"`); }
  };
  const saveTemplate = async () => {
    const name = window.prompt("Name this roster template (e.g. 'Standard Month')");
    if (!name) return;
    try { await api.post("/roster-templates", { name, patterns }); toast.success("Template saved"); loadSaved(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const deleteTemplate = async (id, e) => {
    e.stopPropagation();
    try { await api.delete(`/roster-templates/${id}`); loadSaved(); }
    catch (e2) {}
  };

  const generate = async () => {
    const [y, m] = ym.split("-").map(Number);
    if (!y || !m) { toast.error("Pick a month"); return; }
    if (patterns.length === 0) { toast.error("Add at least one weekly pattern"); return; }
    try {
      const { data } = await api.post("/services/generate-month", { year: y, month: m, patterns, skip_existing: skipExisting });
      toast.success(data.created > 0 ? `${data.created} service${data.created === 1 ? "" : "s"} created` : "No new services (all already existed)");
      setOpen(false); onDone && onDone();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="btn-generate-month" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-[#F7F5F0] font-semibold text-sm hover:border-[#E5C378]/30">
          <CalendarPlus className="w-4 h-4" /> Generate month
        </button>
      </DialogTrigger>
      <DialogContent className="wf-glass border-white/10 max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-[#F7F5F0]">Generate a month of services</DialogTitle></DialogHeader>
        <p className="text-xs text-[#8A97AC] -mt-1">Pick a month and add weekly patterns — one service is created for every matching weekday.</p>

        {savedSets.length > 0 && (
          <Field label="Load a saved template">
            <div className="flex flex-wrap gap-1.5">
              {savedSets.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-[#0B101D] border border-white/10 text-xs text-[#F7F5F0]">
                  <button onClick={() => applySaved(t.id)} data-testid={`load-template-${t.id}`}>{t.name}</button>
                  <button onClick={(e) => deleteTemplate(t.id, e)} className="text-[#6E7D97] hover:text-rose-400"><Trash2 className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3 mt-1">
          <Field label="Month"><input type="month" value={ym} onChange={(e) => setYm(e.target.value)} className={inputCls} data-testid="generate-month-input" /></Field>
          <label className="flex items-end gap-2 pb-2.5 text-xs text-[#B8C1D1]">
            <input type="checkbox" checked={skipExisting} onChange={(e) => setSkipExisting(e.target.checked)} data-testid="generate-skip-existing" /> Skip dates that already have a service
          </label>
        </div>

        <div className="space-y-3 max-h-56 overflow-auto pr-1">
          {patterns.map((p, i) => (
            <div key={i} data-testid={`generate-pattern-${i}`} className="bg-[#0B101D] border border-white/10 rounded-xl p-3 space-y-2">
              <div className="flex items-center justify-between">
                <span className="eyebrow flex items-center gap-1"><Repeat className="w-3 h-3" /> Weekly pattern {i + 1}</span>
                {patterns.length > 1 && <button onClick={() => removeP(i)} className="text-[#6E7D97] hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
              <Field label="Title"><input className={inputCls} value={p.title} onChange={(e) => setP(i, { title: e.target.value })} data-testid={`pattern-title-${i}`} /></Field>
              <div className="grid grid-cols-3 gap-2">
                <Field label="Day">
                  <select className={inputCls} value={p.weekday} onChange={(e) => setP(i, { weekday: Number(e.target.value) })} data-testid={`pattern-weekday-${i}`}>
                    {WEEKDAYS.map(([label, val]) => <option key={val} value={val}>{label}</option>)}
                  </select>
                </Field>
                <Field label="Time"><input type="time" className={inputCls} value={p.time} onChange={(e) => setP(i, { time: e.target.value })} /></Field>
                <Field label="Template">
                  <select className={inputCls} value={p.template} onChange={(e) => setP(i, { template: e.target.value, type: e.target.value })}>
                    {tplNames.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <button onClick={addP} data-testid="add-pattern" className="text-xs text-[#E5C378] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add another pattern</button>
          <button onClick={saveTemplate} data-testid="save-template" className="text-xs text-[#8A97AC] hover:text-[#E5C378]">Save as template</button>
        </div>
        <button onClick={generate} data-testid="confirm-generate-month" className="w-full py-2.5 rounded-xl wf-gold-btn font-semibold text-sm mt-1">Generate services</button>
      </DialogContent>
    </Dialog>
  );
}

/* ================= SERVICE PLANNER ================= */
const FLOW_TYPES = ["Welcome", "Song", "Prayer", "Offering", "Message", "Response", "Communion", "Reading", "Closing"];
const STATUSES = ["draft", "preparing", "ready", "published", "completed"];
const SLOTS = ["Worship Leader", "Keys", "Piano", "Acoustic Guitar", "Electric Guitar", "Bass", "Drums", "Percussion", "Vocals", "BVs", "Sound", "Tech"];

export function ServicePlanner() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [songs, setSongs] = useState([]);
  const [team, setTeam] = useState([]);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const dragIdx = useRef(null);
  const canManage = ["super_admin", "worship_director", "worship_leader"].includes(user?.role);
  const myMemberId = user?.member_id;
  const [avail, setAvail] = useState({});
  const [shareUrl, setShareUrl] = useState("");

  const load = () => api.get(`/services/${id}`).then((r) => setS(r.data));
  useEffect(() => {
    load();
    api.get("/songs").then((r) => setSongs(r.data));
    api.get("/team").then((r) => setTeam(r.data));
    api.get(`/services/${id}/messages`).then((r) => setMessages(r.data));
  }, [id]);
  useEffect(() => {
    if (s?.date) api.get(`/availability/resolve?date=${s.date}`).then((r) => setAvail(r.data)).catch(() => {});
    if (s?.share_enabled && s?.share_id) setShareUrl(`${window.location.origin}/share/${s.share_id}`);
  }, [s?.date, s?.share_enabled, s?.share_id]);
  if (!s) return <div className="text-[#8A97AC]">Loading…</div>;

  const patch = async (fields) => { const { data } = await api.put(`/services/${id}`, fields); setS(data); };

  const reorderFlow = (from, to) => {
    const flow = [...s.flow]; const [m] = flow.splice(from, 1); flow.splice(to, 0, m);
    setS({ ...s, flow }); patch({ flow });
  };
  const addFlowItem = () => { const flow = [...s.flow, { id: crypto.randomUUID(), type: "Song", title: "New item", note: "", song_id: null }]; setS({ ...s, flow }); patch({ flow }); };
  const updateFlowItem = (i, fields) => { const flow = s.flow.map((it, idx) => (idx === i ? { ...it, ...fields } : it)); setS({ ...s, flow }); };
  const commitFlow = () => patch({ flow: s.flow });
  const removeFlowItem = (i) => { const flow = s.flow.filter((_, idx) => idx !== i); setS({ ...s, flow }); patch({ flow }); };

  const addSong = async (songId) => {
    const song = songs.find((x) => x.id === songId); if (!song) return;
    const entry = { id: crypto.randomUUID(), song_id: song.id, title: song.title, key: song.arrangement_key, bpm: song.bpm, time_signature: song.time_signature, order: (s.songs?.length || 0) + 1, notes: "", duration: song.duration };
    const setlist = [...(s.songs || []), entry];
    const flow = [...s.flow, { id: crypto.randomUUID(), type: "Song", title: song.title, note: "", song_id: song.id }];
    setS({ ...s, songs: setlist, flow }); patch({ songs: setlist, flow });
  };
  const removeSong = (sid) => { const setlist = s.songs.filter((x) => x.id !== sid); setS({ ...s, songs: setlist }); patch({ songs: setlist }); };

  const addAssignment = async (memberId, slot) => {
    const m = team.find((x) => x.id === memberId); if (!m) return;
    const { data } = await api.post(`/services/${id}/team`, { member_id: m.id, name: m.name, slot });
    setS({ ...s, team: [...s.team, data] });
  };
  const removeAssignment = async (aid) => { await api.delete(`/services/${id}/team/${aid}`); setS({ ...s, team: s.team.filter((a) => a.id !== aid) }); };
  const respond = async (aid, status) => { const { data } = await api.post(`/services/${id}/team/${aid}/respond`, { status }); setS(data); toast.success(`Marked ${status}`); };

  const sendMsg = async () => { if (!msg.trim()) return; const { data } = await api.post(`/services/${id}/messages`, { text: msg }); setMessages([...messages, data]); setMsg(""); };

  const publish = async () => { await patch({ status: "published" }); toast.success("Service published — team notified"); };

  const deleteService = async () => {
    if (!window.confirm(`Delete "${s.title}"? This can't be undone.`)) return;
    try { await api.delete(`/services/${id}`); toast.success("Service deleted"); nav("/services"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const share = async () => {
    const { data } = await api.post(`/services/${id}/share`);
    const url = `${window.location.origin}/share/${data.share_id}`;
    setShareUrl(url); setS({ ...s, share_enabled: true, share_id: data.share_id });
    try { await navigator.clipboard.writeText(url); toast.success("Share link copied — paste it in WhatsApp"); }
    catch (e) { toast.success("Share link ready"); }
  };

  return (
    <div>
      <button onClick={() => nav("/services")} className="flex items-center gap-1.5 text-sm text-[#8A97AC] hover:text-[#E5C378] mb-4"><ArrowLeft className="w-4 h-4" /> Services</button>

      <div className="bg-gradient-to-br from-[#121B2F] to-[#0A0F1D] border border-[#E5C378]/25 rounded-2xl p-6 mb-6 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-40 h-40 bg-[#E5C378]/10 rounded-full blur-3xl" />
        <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <div className="eyebrow mb-1">{s.type}</div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-white">{s.title}</h1>
            <div className="flex items-center gap-4 text-sm text-[#A0AEC0] mt-2">
              <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {fmtDate(s.date)}</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {s.time}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {canManage ? (
              <select value={s.status} onChange={(e) => patch({ status: e.target.value })} data-testid="service-status-select" className="bg-[#0B101D] border border-white/10 rounded-xl px-3 py-2 text-sm text-[#F7F5F0] capitalize">
                {STATUSES.map((st) => <option key={st} value={st}>{st}</option>)}
              </select>
            ) : <StatusBadge status={s.status} />}
            {canManage && <button onClick={() => nav(`/services/${id}/runsheet`)} data-testid="open-runsheet" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/10 text-[#F7F5F0] font-semibold text-sm"><FileText className="w-4 h-4" /> Run Sheet</button>}
            {canManage && <button onClick={deleteService} data-testid="delete-service" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 font-semibold text-sm"><Trash2 className="w-4 h-4" /> Delete</button>}
            {canManage && <button onClick={share} data-testid="share-service" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#25D366]/15 border border-[#25D366]/40 text-[#7ee6a5] font-semibold text-sm"><Share2 className="w-4 h-4" /> Share</button>}
            {canManage && <button onClick={publish} data-testid="publish-service" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl wf-gold-btn font-semibold text-sm"><Rocket className="w-4 h-4" /> Publish</button>}
          </div>
        </div>
        {shareUrl && (
          <div className="relative mt-4 flex flex-wrap items-center gap-2 bg-[#090D16] border border-white/10 rounded-xl p-3">
            <Link2 className="w-4 h-4 text-[#25D366] shrink-0" />
            <input readOnly value={shareUrl} data-testid="share-url" className="flex-1 min-w-[180px] bg-transparent text-sm text-[#B8C1D1] focus:outline-none" onFocus={(e) => e.target.select()} />
            <button onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); }} className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-[#F7F5F0]">Copy</button>
            <a href={`https://wa.me/?text=${encodeURIComponent(`${s.title} — ${fmtDate(s.date)} ${s.time}\n${shareUrl}`)}`} target="_blank" rel="noreferrer" data-testid="share-whatsapp"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#25D366] text-[#0A0E1A] text-xs font-bold"><MessageCircle className="w-3.5 h-3.5" /> WhatsApp</a>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        {/* Service flow */}
        <div className="lg:col-span-2 space-y-6">
          <div className="wf-card rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-[#F7F5F0]">Service Flow</h3>
              {canManage && <button onClick={addFlowItem} data-testid="add-flow-item" className="text-xs text-[#E5C378] flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Add element</button>}
            </div>
            <div className="space-y-2">
              {s.flow.map((it, i) => {
                const song = it.song_id ? songs.find((x) => x.id === it.song_id) : null;
                return (
                  <div key={it.id} draggable={canManage}
                    onDragStart={() => (dragIdx.current = i)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => { if (dragIdx.current !== null && dragIdx.current !== i) reorderFlow(dragIdx.current, i); dragIdx.current = null; }}
                    data-testid={`flow-item-${i}`}
                    className="bg-[#0B101D] border border-white/5 rounded-xl p-3 flex items-start gap-3 hover:border-[#E5C378]/25">
                    {canManage && <GripVertical className="w-4 h-4 text-[#6E7D97] mt-1 cursor-grab shrink-0" />}
                    <div className="text-xs font-mono text-[#6E7D97] mt-1 w-5 shrink-0">{i + 1}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {canManage ? (
                          <select value={it.type} onChange={(e) => { updateFlowItem(i, { type: e.target.value }); }} onBlur={commitFlow}
                            className="bg-transparent text-sm font-semibold text-[#E5C378] focus:outline-none">
                            {FLOW_TYPES.map((t) => <option key={t} className="bg-[#0B101D]">{t}</option>)}
                          </select>
                        ) : <span className="text-sm font-semibold text-[#E5C378]">{it.type}</span>}
                        {song && <><KeyBadge>{song.arrangement_key}</KeyBadge><BpmBadge>{song.bpm} BPM</BpmBadge></>}
                      </div>
                      {canManage ? (
                        <input value={it.title} onChange={(e) => updateFlowItem(i, { title: e.target.value })} onBlur={commitFlow}
                          className="bg-transparent text-sm text-[#F7F5F0] w-full focus:outline-none mt-0.5" />
                      ) : <div className="text-sm text-[#F7F5F0]">{it.title}</div>}
                      {canManage ? (
                        <input value={it.note || ""} placeholder="Add a note (e.g. 'Pad under prayer')…" onChange={(e) => updateFlowItem(i, { note: e.target.value })} onBlur={commitFlow}
                          className="bg-transparent text-xs text-[#8A97AC] w-full focus:outline-none mt-1 italic" />
                      ) : it.note && <div className="text-xs text-[#8A97AC] italic mt-1">{it.note}</div>}
                    </div>
                    {canManage && <button onClick={() => removeFlowItem(i)} className="text-[#6E7D97] hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Setlist */}
          <div className="wf-card rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-[#F7F5F0]">Setlist</h3>
              {canManage && (
                <Select onValueChange={addSong}>
                  <SelectTrigger className="w-44 bg-[#0B101D] border-white/10 text-xs" data-testid="add-song-to-service"><SelectValue placeholder="+ Add song" /></SelectTrigger>
                  <SelectContent className="bg-[#0B101D] border-white/10 text-[#F7F5F0]">
                    {songs.map((song) => <SelectItem key={song.id} value={song.id}>{song.title} · {song.arrangement_key}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            {(!s.songs || s.songs.length === 0) ? <p className="text-sm text-[#6E7D97]">No songs added yet.</p> : (
              <div className="space-y-2">
                {s.songs.map((song) => (
                  <div key={song.id} className="bg-[#0B101D] border border-white/5 rounded-xl p-3 flex items-center justify-between gap-3">
                    <button onClick={() => nav(`/songs/${song.song_id}`)} className="flex items-center gap-3 min-w-0 text-left">
                      <Music className="w-4 h-4 text-[#E5C378] shrink-0" />
                      <div className="min-w-0"><div className="text-sm text-[#F7F5F0] truncate">{song.title}</div>
                        <div className="flex gap-2 mt-1"><KeyBadge>{song.key}</KeyBadge><BpmBadge>{song.bpm} BPM</BpmBadge></div></div>
                    </button>
                    <div className="flex items-center gap-2">
                      <button onClick={() => nav(`/stage/${song.song_id}`)} className="text-xs text-[#8A97AC] hover:text-[#E5C378]">Stage</button>
                      {canManage && <button onClick={() => removeSong(song.id)} className="text-[#6E7D97] hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Team + chat */}
        <div className="space-y-6">
          <div className="wf-card rounded-2xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-[#F7F5F0]">Team</h3>
              {canManage && <AddAssignmentDialog team={team} availability={avail} onAdd={addAssignment} />}
            </div>
            <div className="space-y-2">
              {s.team.length === 0 && <p className="text-sm text-[#6E7D97]">No one assigned yet.</p>}
              {s.team.map((a) => {
                const mine = a.member_id === myMemberId;
                return (
                  <div key={a.id} className="bg-[#0B101D] border border-white/5 rounded-xl p-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0"><div className="text-sm text-[#F7F5F0] truncate">{a.name}</div><div className="text-xs text-[#E5C378]">{a.slot}</div></div>
                      <div className="flex items-center gap-1.5">
                        {a.status === "confirmed" ? <Check className="w-4 h-4 text-emerald-400" /> : a.status === "declined" ? <X className="w-4 h-4 text-rose-400" /> : <HelpCircle className="w-4 h-4 text-amber-400" />}
                        {canManage && (
                          <select value={a.status} onChange={(e) => respond(a.id, e.target.value)} data-testid={`planner-status-${a.id}`}
                            className="bg-[#0B101D] border border-white/10 rounded-md px-1.5 py-1 text-[10px] text-[#F7F5F0] capitalize">
                            {["pending", "confirmed", "maybe", "declined"].map((st) => <option key={st} value={st}>{st}</option>)}
                          </select>
                        )}
                        {canManage && <button onClick={() => removeAssignment(a.id)} className="text-[#6E7D97] hover:text-rose-400"><Trash2 className="w-3.5 h-3.5" /></button>}
                      </div>
                    </div>
                    {mine && a.status !== "confirmed" && (
                      <div className="flex gap-1.5 mt-2">
                        <button onClick={() => respond(a.id, "confirmed")} data-testid="btn-confirm-attendance" className="flex-1 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 text-xs font-semibold">Confirm</button>
                        <button onClick={() => respond(a.id, "maybe")} className="flex-1 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/40 text-xs font-semibold">Maybe</button>
                        <button onClick={() => respond(a.id, "declined")} data-testid="btn-decline-attendance" className="flex-1 py-1.5 rounded-lg bg-rose-500/15 text-rose-300 border border-rose-500/40 text-xs font-semibold">Decline</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Service chat */}
          <div className="wf-card rounded-2xl p-5">
            <h3 className="font-display font-semibold text-[#F7F5F0] mb-3 flex items-center gap-2"><MessageSquare className="w-4 h-4 text-[#E5C378]" /> Service chat</h3>
            <div className="space-y-2 max-h-56 overflow-auto mb-3">
              {messages.length === 0 && <p className="text-sm text-[#6E7D97]">No messages yet. Keep it worship-team focused.</p>}
              {messages.map((m) => (
                <div key={m.id} className="bg-[#0B101D] border border-white/5 rounded-lg p-2.5">
                  <div className="text-xs text-[#E5C378]">{m.author}</div>
                  <div className="text-sm text-[#F7F5F0]">{m.text}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendMsg()} data-testid="service-chat-input"
                placeholder="Message the team…" className="flex-1 bg-[#0B101D] border border-white/10 rounded-lg px-3 py-2 text-sm text-[#F7F5F0]" />
              <button onClick={sendMsg} data-testid="send-message" className="px-3 rounded-lg wf-gold-btn"><Send className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AddAssignmentDialog({ team, availability = {}, onAdd }) {
  const [open, setOpen] = useState(false);
  const [member, setMember] = useState("");
  const [slot, setSlot] = useState("Vocals");
  const badge = (st) => st === "available" ? "✓ available" : st === "maybe" ? "? maybe" : st === "unavailable" ? "✕ unavailable" : "";
  const sorted = [...team].sort((a, b) => {
    const rank = { available: 0, maybe: 1, unknown: 2, none: 2, unavailable: 3 };
    return (rank[availability[a.id]?.status] ?? 2) - (rank[availability[b.id]?.status] ?? 2);
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="add-assignment" className="text-xs text-[#E5C378] flex items-center gap-1"><UserPlus className="w-3.5 h-3.5" /> Assign</button></DialogTrigger>
      <DialogContent className="wf-glass border-white/10 max-w-sm">
        <DialogHeader><DialogTitle className="font-display text-[#F7F5F0]">Assign team member</DialogTitle></DialogHeader>
        <p className="text-xs text-[#6E7D97] -mt-1">Availability shown for this service date.</p>
        <Field label="Member">
          <select className={inputCls} value={member} onChange={(e) => setMember(e.target.value)} data-testid="assign-member-select">
            <option value="">Select…</option>
            {sorted.map((m) => {
              const st = availability[m.id]?.status;
              return <option key={m.id} value={m.id}>{m.name} ({m.instrument}){st && st !== "unknown" && st !== "none" ? ` — ${badge(st)}` : ""}</option>;
            })}
          </select>
        </Field>
        {member && availability[member]?.status === "unavailable" && (
          <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-lg p-2">This person marked themselves unavailable. Assigning anyway will override it.</div>
        )}
        <Field label="Position"><select className={inputCls} value={slot} onChange={(e) => setSlot(e.target.value)}>{SLOTS.map((x) => <option key={x}>{x}</option>)}</select></Field>
        <button onClick={() => { if (member) { onAdd(member, slot); setOpen(false); setMember(""); } }} data-testid="confirm-assignment" className="w-full py-2.5 rounded-xl wf-gold-btn font-semibold text-sm">Add to team</button>
      </DialogContent>
    </Dialog>
  );
}
